import { useNavigate } from 'react-router-dom';
import { usePresentation } from '../hooks/usePresentation.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import '../styles/screens.css';
import './Home.css';

/**
 * Home — daily entry point.
 * Branded greeting + active project card + toolkit list + bulletin-board link.
 *
 * Layout philosophy: the toolkit is the most important content on this page,
 * so the cards are wide and substantial (not a tiny grid). The page closes
 * with the community bulletin-board link card so the bottom of the page
 * leads outward instead of just trailing into empty space.
 *
 * The toolkit reflects the four-step flow:
 *   Logistics → Objective → Draft → Visuals
 * "Pages" was folded into Logistics, and the old Brainstorm + Design steps
 * were merged into a single "Draft" tool (route /tools/draft). "Visuals" is
 * the deck builder; its internal route stays /tools/builder.
 */

const PADLET_URL = 'https://padlet.com/alisonmceachern/presentation-lab-community-bulletin-board-c1irqee7ya9tvmqd';

const TOOLS = [
  { num: '01', label: 'Logistics', desc: 'Where, when, and who you’re presenting to',   color: 'forest', to: '/tools/logistics', available: true },
  { num: '02', label: 'Objective', desc: 'Define what your audience walks away knowing', color: 'sun',    to: '/tools/objective', available: true },
  { num: '03', label: 'Draft',     desc: 'Brainstorm, group, and flesh out your content', color: 'plum',  to: '/tools/draft',     available: true },
  { num: '04', label: 'Visuals',   desc: 'Assemble everything into your final deck',      color: 'forest', to: '/tools/builder',   available: true },
];

// The step count drives the progress card so it can never drift out of sync
// with the toolkit list again.
const STEP_COUNT = TOOLS.length;

export default function Home() {
  const navigate = useNavigate();
  const { data, activeId, newPlan } = usePresentation();
  const { user } = useAuth();

  // Ensure we always have a plan to edit before sending the user into
  // a tool. If activeId is null (brand-new user), create one now.
  async function startOrContinue() {
    if (!activeId) await newPlan();
    navigate('/tools/logistics');
  }

  // Display the user's typed name (from sign-up) — fall back to the
  // first part of their email if metadata is missing for any reason.
  const userName =
    user?.user_metadata?.name?.split(' ')[0] ||
    user?.email?.split('@')[0] ||
    'there';
  const today = new Date().toLocaleDateString('en-CA', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  // Has the user filled in any Logistics field? Treat that as "in progress."
  const logisticsHasData = Object.keys(data.logistics ?? {}).some(
    (k) => k !== 'av' && data.logistics[k]
  );

  return (
    <div className="screen">
      <header className="screen-head home-head">
        <BrandLogo size={56} className="home-head-logo" />
        <div className="screen-head-text">
          <span className="eyebrow">{today}</span>
          <h1 className="home-welcome">
            Welcome, <em>{userName}</em> — let’s get to it!
          </h1>
        </div>
        <div className="avatar">{userName.slice(0, 2).toUpperCase()}</div>
      </header>

      {logisticsHasData ? (
        <ActiveProjectCard
          eventName={data.logistics.eventName}
          updatedAt={data.updatedAt}
          onContinue={() => navigate('/tools/logistics')}
        />
      ) : (
        <NoActiveProjectCard onStart={startOrContinue} />
      )}

      <section className="home-toolkit">
        <span className="eyebrow">Your toolkit</span>
        <div className="toolkit-list">
          {TOOLS.map((t) => (
            <ToolCard
              key={t.num}
              num={t.num}
              label={t.label}
              desc={t.desc}
              color={t.color}
              available={t.available}
              onClick={() => navigate(t.to)}
            />
          ))}
        </div>
      </section>

      <BulletinBoardLink href={PADLET_URL} />
    </div>
  );
}

function ActiveProjectCard({ eventName, updatedAt, onContinue }) {
  const lastEdited = updatedAt
    ? formatRelative(new Date(updatedAt))
    : 'just now';

  return (
    <div className="card home-active">
      <div className="card-eyebrow">
        <span className="eyebrow" style={{ color: 'var(--forest)' }}>In progress</span>
        <span className="eyebrow">Step 1 / {STEP_COUNT}</span>
      </div>
      <h3>{eventName || 'Untitled presentation'}</h3>
      <div className="progress">
        <div className="progress-fill" style={{ width: `${(1 / STEP_COUNT) * 100}%` }} />
      </div>
      <div className="home-active-foot">
        <span>Logistics · edited {lastEdited}</span>
        <button onClick={onContinue} className="home-active-cta" type="button">
          Continue →
        </button>
      </div>
    </div>
  );
}

function NoActiveProjectCard({ onStart }) {
  return (
    <div className="card home-empty-card">
      <span className="eyebrow">Ready when you are</span>
      <h3>Start your first presentation</h3>
      <p style={{ marginBottom: 'var(--space-3)' }}>
        Four tools, one workbook. Auto-saves as you go.
      </p>
      <button onClick={onStart} className="btn btn-primary" type="button">
        Start a new plan
      </button>
    </div>
  );
}

/**
 * Wide horizontal toolkit card. Each card has a coloured numbered badge
 * on the left, the tool name + description in the middle, and a chevron
 * on the right hinting it's tappable.
 */
function ToolCard({ num, label, desc, color, available, onClick }) {
  return (
    <button
      className={`tool-card ${!available ? 'is-locked' : ''}`}
      data-color={color}
      type="button"
      onClick={onClick}
    >
      <span className="tool-card__num">{num}</span>
      <span className="tool-card__body">
        <span className="tool-card__label">{label}</span>
        <span className="tool-card__desc">{desc}</span>
      </span>
      <span className="tool-card__chevron" aria-hidden="true">›</span>
    </button>
  );
}

/**
 * Bulletin-board link — replaces the previous decorative closing flourish.
 * Now a real anchor tag pointing at the Padlet, opens in a new tab.
 * The bobbing + ripple animation on the smiley signals that the card is
 * tappable; same visual cue we use on the office-hours signup card.
 */
function BulletinBoardLink({ href }) {
  return (
    <a
      className="home-bulletin"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="home-bulletin-icon" aria-hidden="true">
        <span className="home-bulletin-ring" />
        <span className="home-bulletin-ring home-bulletin-ring-2" />
        <BulletinBoardSmiley size={88} />
      </div>
      <div className="home-bulletin-text">
        <div className="home-bulletin-title">Visit the bulletin board</div>
        <div className="home-bulletin-desc">Tips, questions, and shared work</div>
      </div>
      <span className="home-bulletin-arrow" aria-hidden="true">↗</span>
    </a>
  );
}

/**
 * Inline brand logo — three-slide stack with smiley on the front slide.
 * Used in the welcome row at the top of the page.
 */
function BrandLogo({ size = 64, className = '' }) {
  return (
    <svg
      className={`brand-logo ${className}`.trim()}
      width={size}
      height={size}
      viewBox="0 0 220 220"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Yellow back slide */}
      <g transform="translate(110, 124) rotate(-7)">
        <rect x="-92" y="-64" width="184" height="128" rx="11" fill="var(--sun, #e8b547)" />
      </g>
      {/* Terra middle slide */}
      <g transform="translate(112, 112)">
        <rect x="-94" y="-66" width="188" height="132" rx="11" fill="var(--terra, #c45c3a)" />
        <rect x="-94" y="-66" width="188" height="14" fill="var(--terra-stripe, #a0411e)" />
      </g>
      {/* Forest front slide with smiley */}
      <g transform="translate(108, 96) rotate(2)">
        <rect x="-94" y="-66" width="188" height="132" rx="11" fill="var(--forest, #3a8a63)" />
        <rect x="-94" y="-66" width="188" height="14" fill="var(--forest-mid, #1f6a47)" />
        <rect x="-72" y="-30" width="106" height="8"  rx="4" fill="#ffffff" />
        <rect x="-72" y="-12" width="80"  height="6"  rx="3" fill="#ffffff" opacity="0.9" />
        <rect x="-72" y="4"   width="124" height="6"  rx="3" fill="#ffffff" opacity="0.9" />
        <rect x="-72" y="20"  width="64"  height="6"  rx="3" fill="#ffffff" opacity="0.9" />
        <g transform="translate(62, 38)">
          <circle cx="0" cy="0" r="14" fill="#f5c147" />
          <circle cx="-4" cy="-3" r="1.6" fill="#1f6a47" />
          <circle cx="4"  cy="-3" r="1.6" fill="#1f6a47" />
          <path d="M -5 2 Q 0 7 5 2" stroke="#1f6a47" strokeWidth="1.8" strokeLinecap="round" fill="none" />
        </g>
      </g>
    </svg>
  );
}

/**
 * The simple smiley — yellow face, dark green eyes and curve smile.
 * Same character as the bulletin-board image, sized for the link card.
 */
function BulletinBoardSmiley({ size = 88 }) {
  return (
    <svg
      className="bulletin-smiley"
      width={size}
      height={size}
      viewBox="-100 -100 200 200"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="0" cy="0" r="80" fill="#f5c147" />
      <circle cx="-26" cy="-15" r="7" fill="#2a4a37" />
      <circle cx="26"  cy="-15" r="7" fill="#2a4a37" />
      <path
        d="M -32 12 Q 0 42 32 12"
        stroke="#2a4a37"
        strokeWidth="8"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/** Cheap relative-time formatter — good enough for "just now" / "5 min ago" / "yesterday". */
function formatRelative(date) {
  const ms = Date.now() - date.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)   return `${hr} hr ago`;
  const d = Math.round(hr / 24);
  if (d === 1)   return 'yesterday';
  return `${d} days ago`;
}
